import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Layers, Smartphone, DollarSign } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './ArTechnologiesCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './ArTechnologiesCostCalculatorPage/components/ResultsOverview';
import CostBreakdown from './ArTechnologiesCostCalculatorPage/components/CostBreakdown';
import QuickAdjustmentSidebar from './ArTechnologiesCostCalculatorPage/components/QuickAdjustmentSidebar';
import SavedProjectsSidebar from './ArTechnologiesCostCalculatorPage/components/SavedProjectsSidebar';
import TipsSidebar from './ArTechnologiesCostCalculatorPage/components/TipsSidebar';
import InfoContent from './ArTechnologiesCostCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  projectType: 'mobile',
  projectScope: 'small',
  platformCount: '1',
  featureCount: '3',
  designComplexity: 'medium',
  contentType: 'basic',
  objectCount: '5',
  includeBackend: false,
  backendComplexity: 'simple',
  includeCMS: false,
  includeAnalytics: false,
  includeLocalization: false,
  localizationLanguages: '1',
  developmentRegion: 'east_europe',
  developmentTeam: 'mixed',
  timelineMonths: '3',
  hasExistingAssets: false,
  includeMaintenancePlan: false,
  maintenanceDuration: '12', // месяцев
  maintenanceLevel: 'basic',
};

// Определение типов AR-проектов
export const projectTypes = [
  { id: 'mobile', name: 'Мобильное AR-приложение', defaultScope: 'small', basePrice: 15000 },
  { id: 'web', name: 'Веб-AR', defaultScope: 'small', basePrice: 10000 },
  { id: 'headset', name: 'AR для гарнитур', defaultScope: 'medium', basePrice: 25000 },
  { id: 'glasses', name: 'AR-очки', defaultScope: 'medium', basePrice: 30000 },
  { id: 'installation', name: 'AR-инсталляция', defaultScope: 'large', basePrice: 35000 },
  { id: 'industrial', name: 'Промышленное AR-решение', defaultScope: 'large', basePrice: 45000 }
];

// Определение объемов проекта
export const projectScopes = [
  { id: 'small', name: 'Малый', factor: 1, description: 'Одна основная функция с минимальным набором возможностей' },
  { id: 'medium', name: 'Средний', factor: 1.75, description: 'Несколько связанных функций, более сложный UX' },
  { id: 'large', name: 'Большой', factor: 3, description: 'Полнофункциональное приложение с расширенными возможностями' },
  { id: 'enterprise', name: 'Корпоративный', factor: 5, description: 'Комплексное решение для предприятия с интеграциями' }
];

// Определение типов контента
export const contentTypes = [
  { id: 'basic', name: 'Базовый', factor: 1, description: 'Простые 3D-модели и базовые эффекты' },
  { id: 'interactive', name: 'Интерактивный', factor: 1.5, description: 'Интерактивные 3D-модели с анимацией' },
  { id: 'advanced', name: 'Продвинутый', factor: 2, description: 'Сложные модели, анимации и спецэффекты' },
  { id: 'photorealistic', name: 'Фотореалистичный', factor: 2.5, description: 'Высококачественные реалистичные модели и окружение' }
];

// Определение сложности дизайна
export const designComplexities = [
  { id: 'simple', name: 'Простой', factor: 0.8, description: 'Базовый UI, стандартные компоненты' },
  { id: 'medium', name: 'Средний', factor: 1, description: 'Кастомизированный UI, адаптивный дизайн' },
  { id: 'complex', name: 'Сложный', factor: 1.5, description: 'Уникальный UI/UX, анимированные интерфейсы' },
  { id: 'premium', name: 'Премиум', factor: 2, description: 'Высококлассный дизайн, индивидуальная графика и анимации' }
];

// Определение регионов разработки
export const developmentRegions = [
  { id: 'west_europe', name: 'Западная Европа', factor: 1.8, description: 'Высокие ставки, премиальное качество' },
  { id: 'east_europe', name: 'Восточная Европа', factor: 1, description: 'Оптимальное соотношение цены и качества' },
  { id: 'north_america', name: 'Северная Америка', factor: 2, description: 'Высокие ставки, ведущие технологии' },
  { id: 'south_asia', name: 'Южная Азия', factor: 0.6, description: 'Доступные ставки, подходит для простых проектов' },
  { id: 'east_asia', name: 'Восточная Азия', factor: 1.2, description: 'Среднерыночные ставки, хорошее качество' }
];

// Определение типов команд
export const developmentTeams = [
  { id: 'agency', name: 'Агентство', factor: 1.5, description: 'Полный цикл разработки с управлением проектом' },
  { id: 'freelancers', name: 'Фрилансеры', factor: 0.7, description: 'Экономично, но требует больше управления' },
  { id: 'mixed', name: 'Смешанная команда', factor: 1, description: 'Баланс стоимости и удобства управления' },
  { id: 'in_house', name: 'Внутренняя команда', factor: 1.2, description: 'Добавляет затраты на наём и управление' }
];

// Определение уровней сложности бэкенда
export const backendComplexities = [
  { id: 'simple', name: 'Простой', factor: 0.8, description: 'Базовое хранение данных и авторизация' },
  { id: 'medium', name: 'Средний', factor: 1, description: 'API, интеграции с внешними сервисами' },
  { id: 'complex', name: 'Сложный', factor: 1.6, description: 'Многоуровневая архитектура, сложная бизнес-логика' },
  { id: 'enterprise', name: 'Корпоративный', factor: 2.5, description: 'Корпоративная интеграция, высокая нагрузка' }
];

// Определение уровней обслуживания
export const maintenanceLevels = [
  { id: 'basic', name: 'Базовый', factor: 0.1, description: 'Исправление ошибок и базовые обновления' },
  { id: 'standard', name: 'Стандартный', factor: 0.15, description: 'Техподдержка и регулярные обновления' },
  { id: 'premium', name: 'Премиум', factor: 0.25, description: 'Приоритетная поддержка и новые функции' },
  { id: 'vip', name: 'VIP', factor: 0.4, description: 'Выделенная команда, непрерывное улучшение' }
];

/**
 * ArTechnologiesCostCalculatorPage Component - Калькулятор стоимости AR-технологий
 */
const ArTechnologiesCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('ar-technologies-cost-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости AR-технологий | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте стоимость разработки AR-приложений и технологий: мобильные приложения, веб-AR, решения для AR-очков и гарнитур, корпоративные внедрения и промышленные решения.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState([]);
  const [showFullBreakdown, setShowFullBreakdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProjects, setSavedProjects] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных проектов из localStorage ---
  useEffect(() => {
    const savedProjectsData = localStorage.getItem('savedArProjects');
    if (savedProjectsData) {
      try {
        setSavedProjects(JSON.parse(savedProjectsData));
      } catch (e) {
        console.error("Error loading saved projects:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'mobile':
          newState.projectType = 'mobile';
          break;
        case 'web':
          newState.projectType = 'web';
          break;
        case 'headset':
          newState.projectType = 'headset';
          break;
        case 'glasses':
          newState.projectType = 'glasses';
          break;
        case 'installation':
          newState.projectType = 'installation';
          break;
        case 'industrial':
          newState.projectType = 'industrial';
          break;
        case 'enterprise':
          newState.projectType = 'mobile';
          newState.projectScope = 'enterprise';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setCostBreakdown([]);
    setErrors({});
    setShowFullBreakdown(false);

  }, [subToolId]);

  // Сохранение проекта в localStorage
  const saveProject = (name) => {
    if (!results) return;

    const newSavedProject = {
      id: Date.now().toString(),
      name: name || `AR-проект ${projectTypes.find(t => t.id === formData.projectType)?.name}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProjects = [...savedProjects, newSavedProject];
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedArProjects', JSON.stringify(updatedSavedProjects));

    return newSavedProject;
  };

  // Удаление сохраненного проекта
  const deleteSavedProject = (id) => {
    const updatedSavedProjects = savedProjects.filter(project => project.id !== id);
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedArProjects', JSON.stringify(updatedSavedProjects));
  };

  // Загрузка сохраненного проекта
  const loadSavedProject = (id) => {
    const projectToLoad = savedProjects.find(project => project.id === id);
    if (projectToLoad) {
      setFormData(projectToLoad.formData);
      setResults(projectToLoad.results);
      
      // Воссоздаём breakdown на основе результатов
      if (projectToLoad.results) {
        const categories = ['development', 'design', 'content', 'backend', 'maintenance'];
        const breakdown = categories.map(category => {
          const cost = projectToLoad.results.categoryCosts[category] || 0;
          return {
            category,
            cost,
            percentage: (cost / projectToLoad.results.totalCost) * 100
          };
        }).filter(item => item.cost > 0);
        
        setCostBreakdown(breakdown);
      }
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Smartphone className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости AR-технологий'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              costBreakdown={costBreakdown}
              setCostBreakdown={setCostBreakdown}
              projectTypes={projectTypes}
              projectScopes={projectScopes}
              contentTypes={contentTypes}
              designComplexities={designComplexities}
              developmentRegions={developmentRegions}
              developmentTeams={developmentTeams}
              backendComplexities={backendComplexities}
              maintenanceLevels={maintenanceLevels}
              saveProject={saveProject}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <CostBreakdown 
                  breakdown={costBreakdown} 
                  results={results}
                  showFullBreakdown={showFullBreakdown}
                  setShowFullBreakdown={setShowFullBreakdown}
                />
              </>
            )}

            {/* --- Информационный контент об AR-технологиях --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              projectTypes={projectTypes}
              projectScopes={projectScopes}
            />

            {/* --- Панель сохраненных проектов --- */}
            <SavedProjectsSidebar 
              savedProjects={savedProjects}
              deleteSavedProject={deleteSavedProject}
              loadSavedProject={loadSavedProject}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Cost Breakdown Table */
        .cost-breakdown-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .cost-breakdown-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default ArTechnologiesCostCalculatorPage;